import type { DocumentReference } from 'fhir/r4';
import { useState, useCallback } from 'react';

import FHIRUpdater from '../../../../data/FHIRUpdater';
import { fetchMemberDocumentWithAttachment } from '../documentReferenceQueries';

export interface DeleteMemberDocumentParams {
  /** Auth token for the FHIR proxy. */
  token: string;
  /** Patient ID - used to fetch the resource and route PUT to the correct FHIR store. */
  patientId: string;
  /** DocumentReference id to soft-delete. */
  documentId: string;
  /** Managing org ID (payor) - used in extension to route PUT to the correct FHIR store. */
  org?: string | undefined;
  /** Name of the practitioner performing the soft delete (for last-modified-by identifier). */
  practitionerName?: string | undefined;
}

export interface UseMemberDocumentDeleteResult {
  /** Soft-delete a member document by setting status to entered-in-error via FHIR PUT. */
  deleteDocument: (params: DeleteMemberDocumentParams) => Promise<void>;
  /** Whether a delete is in progress. */
  isDeleting: boolean;
  /** Error message from the last failed delete. */
  error: string | null;
  /** Clear the error state. */
  clearError: () => void;
}

/**
 * Hook for soft-deleting member documents by setting DocumentReference status to entered-in-error.
 * Uses FHIR PUT. Loads the full DocumentReference before updating status so the payor store
 * receives a complete resource (list searches return metadata only).
 *
 * @example
 * ```tsx
 * const { deleteDocument, isDeleting, error } = useMemberDocumentDelete();
 * await deleteDocument({
 *   token: authInfo.token,
 *   patientId: patientId,
 *   documentId: document.id,
 * });
 * ```
 */
export function useMemberDocumentDelete(): UseMemberDocumentDeleteResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const deleteDocument = useCallback(
    async ({ token, patientId, documentId, org, practitionerName }: DeleteMemberDocumentParams) => {
      setError(null);
      setIsDeleting(true);

      try {
        if (!documentId) {
          throw new Error('Document has no id.');
        }

        const resource = await fetchMemberDocumentWithAttachment(token, documentId, patientId);
        const id = resource.id;
        if (!id) {
          throw new Error('Document has no id.');
        }

        const orgExtension = {
          url: 'https://projectwell.io/fhir/orgid',
          valueString: org ?? 'pw',
        };
        const existingExtensions = resource.extension ?? [];
        const otherExtensions = existingExtensions.filter((e) => e.url !== orgExtension.url);
        const extensions = [orgExtension, ...otherExtensions];

        const lastModifiedByIdentifier = {
          system: 'https://projectwell.io/fhir/identifiers/last-modified-by',
          value: practitionerName,
        };
        const existingIdentifiers = resource.identifier ?? [];
        const otherIdentifiers = existingIdentifiers.filter(
          (i) => i.system !== lastModifiedByIdentifier.system
        );
        const identifiers = [lastModifiedByIdentifier, ...otherIdentifiers];

        const payload: DocumentReference = {
          ...resource,
          status: 'entered-in-error',
          subject: { reference: `Patient/${patientId}` },
          extension: extensions,
          ...(practitionerName && { identifier: identifiers }),
        };

        await FHIRUpdater(`/DocumentReference/${id}`, token, payload);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to delete document.';
        setError(message);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    []
  );

  return { deleteDocument, isDeleting, error, clearError };
}
